import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Sale, SaleItem, Product } from '../types';

export class SalesService {
  // Create a new sale
  static async createSale(
    salonId: string,
    staffId: string,
    items: SaleItem[],
    paymentMethod: Sale['paymentMethod'],
    customerId?: string,
    notes?: string
  ): Promise<string> {
    try {
      // Calculate total amount
      const totalAmount = items.reduce((sum, item) => sum + item.totalPrice, 0);

      // Create sale document
      const saleData = {
        salonId,
        customerId,
        staffId,
        items: items.map(item => ({
          ...item,
          id: item.id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        })),
        totalAmount,
        paymentMethod,
        notes,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'sales'), saleData);

      // Update inventory for product sales
      await this.updateInventoryForSale(saleData.items, salonId);

      // Update customer record if customer provided
      if (customerId) {
        await this.updateCustomerRecord(customerId, totalAmount);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating sale:', error);
      throw new Error('Failed to create sale');
    }
  }

  // Update inventory when items are sold
  private static async updateInventoryForSale(items: SaleItem[], salonId: string): Promise<void> {
    const productUpdates: { [productId: string]: number } = {};

    // Group product sales by product ID
    items.forEach(item => {
      if (item.type === 'product' && item.productId) {
        productUpdates[item.productId] = (productUpdates[item.productId] || 0) + item.quantity;
      }
    });

    // Update each product's stock
    const updatePromises = Object.entries(productUpdates).map(async ([productId, soldQuantity]) => {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);

      if (productSnap.exists()) {
        const product = productSnap.data() as Product;
        const newStock = product.currentStock - soldQuantity;

        await updateDoc(productRef, {
          currentStock: Math.max(0, newStock), // Prevent negative stock
          updatedAt: Timestamp.now(),
        });
      }
    });

    await Promise.all(updatePromises);
  }

  // Update customer spending and visit count
  private static async updateCustomerRecord(customerId: string, amount: number): Promise<void> {
    const customerRef = doc(db, 'customers', customerId);
    const customerSnap = await getDoc(customerRef);

    if (customerSnap.exists()) {
      const customer = customerSnap.data();
      await updateDoc(customerRef, {
        totalSpent: customer.totalSpent + amount,
        visitCount: customer.visitCount + 1,
        lastVisit: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }
  }

  // Get sales for a salon within a date range
  static async getSales(
    salonId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<Sale[]> {
    try {
      let q = query(
        collection(db, 'sales'),
        where('salonId', '==', salonId)
      );

      const querySnapshot = await getDocs(q);
      let sales: Sale[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sales.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Sale);
      });

      // Sort client-side instead of requiring composite index
      sales = sales.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      // Client-side date filtering if needed
      let filteredSales = sales;
      if (startDate) {
        filteredSales = filteredSales.filter(sale => sale.createdAt >= startDate);
      }
      if (endDate) {
        filteredSales = filteredSales.filter(sale => sale.createdAt <= endDate);
      }

      return filteredSales;
    } catch (error) {
      console.error('Error getting sales:', error);
      throw new Error('Failed to get sales');
    }
  }

  // Get today's sales total
  static async getTodaySalesTotal(salonId: string): Promise<number> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todaySales = await this.getSales(salonId, today, tomorrow);
    return todaySales.reduce((total, sale) => total + sale.totalAmount, 0);
  }

  // Get sale by ID
  static async getSale(saleId: string): Promise<Sale | null> {
    try {
      const docRef = doc(db, 'sales', saleId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Sale;
      }
      return null;
    } catch (error) {
      console.error('Error getting sale:', error);
      throw new Error('Failed to get sale');
    }
  }
}
